import React, { useState, useRef, useCallback } from 'react';
import classNames from 'classnames';
import { format } from 'date-fns';
import { FiCalendar } from 'react-icons/fi';

import { useClickOutside } from '../../hooks/useClickOutside';

import { CalendarPanel } from './CalendarPanel';
import stylesForm from './FormInput.module.scss';
import styles from './DateInput.module.scss';

interface DateInputProps {
  id: string;
  label: string;
  value: Date | null;
  onChange: (value: Date | null) => void;
  error?: string | null;
  required?: boolean;
}

export const DateInput: React.FC<DateInputProps> = ({ id, label, value, onChange, error, required = false }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const closeDropdown = useCallback(() => setOpen(false), []);
  useClickOutside(wrapperRef, closeDropdown);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') setOpen(false);
  }, []);

  const handleSelect = useCallback(
    (date: Date) => {
      onChange(date);
      setOpen(false);
    },
    [onChange],
  );

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={classNames(stylesForm.formInput, { [stylesForm.formInputError]: error })}
      ref={wrapperRef}
      onKeyDown={handleKeyDown}
    >
      <label htmlFor={id} className={stylesForm.label}>
        {label}
        {required && <span className={stylesForm.required}>*</span>}
      </label>

      <div className={styles.wrapper}>
        <button
          type="button"
          id={id}
          className={classNames(styles.trigger, { [styles.triggerError]: error })}
          onClick={() => setOpen(prev => !prev)}
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          {value ? (
            <span>{format(value, 'dd.MM.yyyy')}</span>
          ) : (
            <span className={styles.triggerPlaceholder}>дд.мм.гггг</span>
          )}
          <FiCalendar size={16} className={styles.triggerIcon} />
        </button>

        {open && (
          <div className={styles.dropdown}>
            <CalendarPanel selectedDate={value} onSelect={handleSelect} />
          </div>
        )}
      </div>

      {error && <p className={stylesForm.error}>{error}</p>}
    </div>
  );
};
